import {
  createNode,
  findClosingBracket,
  findClosingSequence,
  normaliseCodeSpan,
  parseLinkDestination,
  parseTrailingClassAnnotation,
} from './mdUtils.js';
import { collectList } from './mdLists.js';
import { parseAlignmentRow, splitTableCells } from './mdTable.js';
import { getColorConfig, extractColorToken } from '../mdTheme.js';

function extractLeadingColorTokenFromText(value = '') {
  if (typeof value !== 'string') {
    return null;
  }
  const match = value.match(/^\s*\{\s*([A-Za-z]+)\s*\}(.*)$/s);
  if (!match) {
    return null;
  }
  return {
    token: match[1]?.toLowerCase() ?? null,
    remainder: match[2] ?? '',
  };
}

function applyInlineColorMetadata(nodes = []) {
  if (!Array.isArray(nodes) || nodes.length === 0) {
    return nodes;
  }
  for (let index = 0; index < nodes.length; index += 1) {
    const node = nodes[index];
    if (!node || !INLINE_COLOR_TARGETS.has(node.type)) {
      continue;
    }
    const nextNode = nodes[index + 1];
    if (!nextNode || nextNode.type !== NodeType.TEXT) {
      continue;
    }
    const extracted = extractLeadingColorTokenFromText(
      nextNode.props?.value ?? ''
    );
    if (!extracted || !extracted.token) {
      continue;
    }
    if (!getColorConfig(extracted.token)) {
      continue;
    }
    node.props = { ...(node.props || {}), color: extracted.token };
    const remainder = extracted.remainder ?? '';
    if (remainder) {
      nextNode.props = { ...(nextNode.props || {}), value: remainder };
    } else {
      nodes.splice(index + 1, 1);
    }
  }
  return nodes;
}

export const BlockTokenType = {
  HEADING: 'heading',
  PARAGRAPH: 'paragraph',
  BLOCKQUOTE: 'blockquote',
  ORDERED_LIST: 'ordered_list',
  UNORDERED_LIST: 'unordered_list',
  COLLAPSE: 'collapse',
  FENCED_CODE: 'fenced_code', // use to style language-specific syntax
  INDENTED_CODE: 'indented_code',
  THEMATIC_BREAK: 'thematic_break',
  TABLE: 'table',
};

export const NodeType = {
  ...BlockTokenType,
  DOCUMENT: 'document',
  TEXT: 'text',
  EMPHASIS: 'emphasis',
  STRONG: 'strong',
  HIGHLIGHT: 'highlight',
  CODE_SPAN: 'code_span',
  LINE_BREAK: 'line_break',
  IMAGE: 'image',
  LINK: 'link',
  LIST_ITEM: 'list_item',
  UNDERLINE: 'underline',
  STRIKETHROUGH: 'strikethrough',
  CHECKBOX: 'checkbox',
  TABLE_HEADER: 'table_header',
  TABLE_BODY: 'table_body',
  TABLE_ROW: 'table_row',
  TABLE_CELL: 'table_cell',
  COLLAPSE: 'collapse',
  ICON: 'icon',
  SELECT: 'select',
};

const INLINE_COLOR_TARGETS = new Set([
  NodeType.EMPHASIS,
  NodeType.STRONG,
  NodeType.STRIKETHROUGH,
  NodeType.UNDERLINE,
  NodeType.HIGHLIGHT,
  NodeType.LINK,
  NodeType.TABLE_ROW,
]);

function peelValidColorToken(value) {
  const { text, token } = extractColorToken(value);
  if (token && getColorConfig(token)) {
    return { text, color: token };
  }
  return { text: value, color: null };
}

function parseThematicBreakMeta(line_) {
  const trimmed = line_.trim();
  const { text, color } = peelValidColorToken(trimmed);
  const base = color ? text : trimmed;
  if (base.length < 3) {
    return null;
  }
  if (!/^(-|\*|_)/.test(base)) {
    return null;
  }
  const chars = base.replace(/\s+/g, '');
  if (chars.length < 3) {
    return null;
  }
  const firstChar = chars[0];
  if (!/[-*_]/.test(firstChar)) {
    return null;
  }
  if (!/^[-*_]+$/.test(chars)) {
    return null;
  }
  return {
    color: color ?? null,
  };
}

export function consumeThematicBreak(lines_, index_) {
  const meta = parseThematicBreakMeta(lines_[index_]);
  if (!meta) {
    return null;
  }
  return {
    token: {
      type: BlockTokenType.THEMATIC_BREAK,
      color: meta.color ?? null,
    },
    nextIndex: index_ + 1,
  };
}

export function consumeFencedCode(lines_, index_) {
  const line = lines_[index_];
  const trimmed = line.trim();
  if (!/^```/.test(trimmed)) {
    return null;
  }

  const fenceMatch = trimmed.match(/^```(\w+)?\s*$/);
  const fenceLang = fenceMatch?.[1] ?? '';
  const codeLines = [];
  let nextIndex = index_ + 1;
  while (nextIndex < lines_.length && !/^```/.test(lines_[nextIndex])) {
    codeLines.push(lines_[nextIndex]);
    nextIndex += 1;
  }
  if (nextIndex < lines_.length && /^```/.test(lines_[nextIndex])) {
    nextIndex += 1;
  }
  return {
    token: {
      type: BlockTokenType.FENCED_CODE,
      content: codeLines.join('\n'),
      lang: fenceLang,
    },
    nextIndex: nextIndex,
  };
}

export function consumeIndentedCode(lines_, index_) {
  const line = lines_[index_];
  if (!/^( {4}|\t)/.test(line)) {
    return null;
  }

  const codeLines = [];
  let nextIndex = index_;
  while (nextIndex < lines_.length) {
    const current = lines_[nextIndex];
    if (/^( {4}|\t)/.test(current)) {
      codeLines.push(current.replace(/^( {4}|\t)/, ''));
      nextIndex += 1;
      continue;
    }
    if (current.trim() === '') {
      codeLines.push('');
      nextIndex += 1;
      continue;
    }
    break;
  }

  return {
    token: {
      type: BlockTokenType.INDENTED_CODE,
      content: codeLines.join('\n'),
    },
    nextIndex: nextIndex,
  };
}

export function consumeList(lines_, index_) {
  const collected = collectList(lines_, index_);
  if (!collected) {
    return null;
  }
  return {
    token: collected,
    nextIndex: collected.nextIndex,
  };
}

export function consumeTable(lines_, index_) {
  if (index_ + 1 >= lines_.length) {
    return null;
  }
  const headerLine = lines_[index_];
  const alignmentLine = lines_[index_ + 1];
  if (!headerLine.includes('|') || !alignmentLine.includes('-')) {
    return null;
  }

  const headerCells = splitTableCells(headerLine);
  const alignments = parseAlignmentRow(alignmentLine);
  if (!alignments || headerCells.length === 0) {
    return null;
  }
  if (alignments.length !== headerCells.length) {
    return null;
  }

  const rows = [];
  let nextIndex = index_ + 2;
  while (nextIndex < lines_.length) {
    const candidate = lines_[nextIndex];
    if (!candidate.trim()) {
      break;
    }
    if (!candidate.includes('|')) {
      break;
    }
    const rowColorMeta = peelValidColorToken(candidate);
    const baseLine = rowColorMeta?.text ?? candidate;
    const rowCells = splitTableCells(baseLine);
    if (rowCells.length === 1 && rowCells[0] === '') {
      break;
    }
    if (rowCells.length < headerCells.length) {
      while (rowCells.length < headerCells.length) {
        rowCells.push('');
      }
    } else if (rowCells.length > headerCells.length) {
      rowCells.length = headerCells.length;
    }
    rows.push({ cells: rowCells, color: rowColorMeta?.color ?? null });
    nextIndex += 1;
  }

  if (rows.length === 0) {
    return null;
  }

  return {
    token: {
      type: BlockTokenType.TABLE,
      header: headerCells,
      rows: rows,
      align: alignments,
    },
    nextIndex: nextIndex,
  };
}

export function consumeCollapse(lines_, index_) {
  const line = lines_[index_] ?? '';
  const match = line.match(/^\s*:::\s*collapse\b(.*)$/i);
  if (!match) {
    return null;
  }
  const rawTitle = (match[1] ?? '').trim();
  const { text, color } = peelValidColorToken(rawTitle);
  const title = (color ? text : rawTitle).trim() || 'Section';

  const contentLines = [];
  let nextIndex = index_ + 1;
  while (nextIndex < lines_.length) {
    const current = lines_[nextIndex] ?? '';
    if (/^\s*:::\s*$/.test(current)) {
      return {
        token: {
          type: BlockTokenType.COLLAPSE,
          title,
          color: color ?? null,
          content: contentLines.join('\n'),
        },
        nextIndex: nextIndex + 1,
      };
    }
    contentLines.push(current);
    nextIndex += 1;
  }
  // Unclosed collapse block: treat as plain text by declining this detector.
  return null;
}

export function consumeBlockquote(lines_, index_) {
  if (!lines_[index_].trim().startsWith('>')) {
    return null;
  }
  let nextIndex = index_;
  const quoteLines = [];
  let detectedColor = null;
  while (nextIndex < lines_.length) {
    const currentTrimmed = lines_[nextIndex].trim();
    if (!currentTrimmed.startsWith('>')) {
      break;
    }
    const lineWithoutMarker = currentTrimmed.replace(/^>\s?/, '');
    const { text, color } = peelValidColorToken(lineWithoutMarker);
    if (!detectedColor && color) {
      detectedColor = color;
    }
    quoteLines.push(color ? text : lineWithoutMarker);
    nextIndex += 1;
  }
  return {
    token: {
      type: BlockTokenType.BLOCKQUOTE,
      content: quoteLines.join('\n'),
      color: detectedColor,
    },
    nextIndex: nextIndex,
  };
}

export function consumeHeading(lines_, index_) {
  const currentLine = lines_[index_];
  const trimmed = currentLine.trim();

  const atxMatch = trimmed.match(/^(#{1,6})\s+(.*)$/);
  if (atxMatch) {
    const atxContent = atxMatch[2].replace(/\s#+\s*$/, '');
    const { text, color } = peelValidColorToken(atxContent);
    const normalizedContent = (color ? text : atxContent).trim();
    return {
      token: {
        type: BlockTokenType.HEADING,
        depth: atxMatch[1].length,
        content: normalizedContent,
        color: color,
      },
      nextIndex: index_ + 1,
    };
  }

  if (index_ + 1 >= lines_.length) {
    return null;
  }

  const nextLine = lines_[index_ + 1];
  const setextMatch = nextLine.match(/^\s*([=-])\1*\s*$/);
  if (!setextMatch) {
    return null;
  }

  const depth = setextMatch[1] === '=' ? 1 : 2;
  const { text, color } = peelValidColorToken(trimmed);
  const normalizedContent = (color ? text : trimmed).trim();
  return {
    token: {
      type: BlockTokenType.HEADING,
      depth: depth,
      content: normalizedContent,
      color: color,
    },
    nextIndex: index_ + 2,
  };
}

export function parseInline(content_ = '') {
  if (!content_) {
    return [];
  }

  const nodes = [];
  let textBuffer = '';
  let index = 0;
  const length = content_.length;

  // Fast handlers for fixed-length delimiters (strikethrough ~~, underline ++)
  const delimiterHandlers = {
    '~~': () =>
      wrapDelimiter('~', 2, children_ =>
        nodes.push(createNode(NodeType.STRIKETHROUGH, {}, children_))
      ),
    '++': () =>
      wrapDelimiter('+', 2, children_ =>
        nodes.push(createNode(NodeType.UNDERLINE, {}, children_))
      ),
    '==': () =>
      wrapDelimiter('=', 2, children_ =>
        nodes.push(createNode(NodeType.HIGHLIGHT, {}, children_))
      ),
  };

  // Resolve emphasis/strong sequences introduced by * or _
  const handleEmphasisAndStrong = markerChar_ => {
    let markerLen = 1;
    while (
      index + markerLen < length &&
      content_[index + markerLen] === markerChar_
    ) {
      markerLen += 1;
    }

    let handled = false;
    if (markerLen >= 3) {
      handled = wrapDelimiter(markerChar_, 3, children_ => {
        const emphasisNode = createNode(NodeType.EMPHASIS, {}, children_);
        nodes.push(createNode(NodeType.STRONG, {}, [emphasisNode]));
      });
    }
    if (!handled && markerLen >= 2) {
      handled = wrapDelimiter(markerChar_, 2, children_ => {
        nodes.push(createNode(NodeType.STRONG, {}, children_));
      });
    }
    if (!handled) {
      handled = wrapDelimiter(markerChar_, 1, children_ => {
        nodes.push(createNode(NodeType.EMPHASIS, {}, children_));
      });
    }
    return handled;
  };

  const pushText = () => {
    if (textBuffer) {
      nodes.push(createNode(NodeType.TEXT, { value: textBuffer }));
      textBuffer = '';
    }
  };

  const wrapDelimiter = (markerChar_, repeatCount_, createNodeFn_) => {
    const marker = markerChar_.repeat(repeatCount_);
    const closingIndex = findClosingSequence(
      content_,
      index + repeatCount_,
      marker
    );
    if (closingIndex === -1) {
      return false;
    }
    const inner = content_.slice(index + repeatCount_, closingIndex);
    if (!inner) {
      return false;
    }
    pushText();
    const children = parseInline(inner);
    createNodeFn_(children);
    index = closingIndex + repeatCount_;
    return true;
  };

  while (index < length) {
    const char = content_[index];

    if (char === '{' && content_.slice(index, index + 9) === '{{select:') {
      const closingIndex = content_.indexOf('}}', index + 9);
      if (closingIndex !== -1) {
        const rawBody = content_.slice(index + 9, closingIndex).trim();
        const parts = rawBody
          .split('|')
          .map(part => part.trim())
          .filter(Boolean);
        if (parts.length >= 2) {
          const current = parts[0];
          const options = parts.slice(1);
          pushText();
          nodes.push(
            createNode(NodeType.SELECT, {
              current,
              options,
            })
          );
          index = closingIndex + 2;
          continue;
        }
      }
    }

    // Escaped sequences (e.g. \*)
    if (char === '\\') {
      if (index + 1 < length) {
        textBuffer += content_[index + 1];
        index += 2;
        continue;
      }
      index += 1;
      continue;
    }

    // Hard line break marker
    if (char === '\n') {
      pushText();
      nodes.push(createNode(NodeType.LINE_BREAK));
      index += 1;
      continue;
    }

    if (
      char === ':' &&
      content_
        .slice(index, index + 6)
        .toLowerCase()
        .startsWith(':icon')
    ) {
      const iconMatch = content_
        .slice(index)
        .match(/^:icon\.?([a-zA-Z0-9_-]+)?:/);
      if (iconMatch) {
        const slug = (iconMatch[1] || '').toLowerCase();
        pushText();
        nodes.push(
          createNode(NodeType.ICON, {
            slug,
          })
        );
        index += iconMatch[0].length;
        continue;
      }
    }

    if (char === '[') {
      const closeIndex = content_.indexOf(']', index + 1);
      if (closeIndex !== -1) {
        const markerContent = content_
          .slice(index + 1, closeIndex)
          .trim()
          .toLowerCase();
        if (markerContent === '' || markerContent === 'x') {
          const isChecked = markerContent === 'x';
          let labelStart = closeIndex + 1;
          if (content_[labelStart] === ' ') {
            labelStart += 1;
          }
          let endIndex = labelStart;
          while (endIndex < length && content_[endIndex] !== '\n') {
            endIndex += 1;
          }
          const rawLabel = content_.slice(labelStart, endIndex).trim();
          pushText();
          nodes.push(
            createNode(
              NodeType.CHECKBOX,
              {
                checked: isChecked,
                rawLabel,
              },
              parseInline(rawLabel)
            )
          );
          index = endIndex;
          if (content_[index] === '\n') {
            index += 1;
          }
          continue;
        }
      }
    }

    // Image : ![alt](src)
    if (char === '!' && content_[index + 1] === '[') {
      const altEnd = findClosingBracket(content_, index + 2, '[', ']');
      if (altEnd !== -1 && content_[altEnd + 1] === '(') {
        const srcEnd = findClosingBracket(content_, altEnd + 2, '(', ')');
        if (srcEnd !== -1) {
          const alt = content_.slice(index + 2, altEnd);
          const destinationRaw = content_.slice(altEnd + 2, srcEnd).trim();
          const destinationMeta = parseLinkDestination(destinationRaw);
          const trailingClassMeta = parseTrailingClassAnnotation(
            content_,
            srcEnd + 1
          );
          const combinedClasses = [
            destinationMeta.classes,
            trailingClassMeta.classes,
          ]
            .filter(Boolean)
            .join(' ')
            .trim();
          pushText();
          nodes.push(
            createNode(NodeType.IMAGE, {
              alt: alt,
              src: destinationMeta.href,
              title: destinationMeta.title,
              classes: combinedClasses,
            })
          );
          index = trailingClassMeta.nextIndex;
          continue;
        }
      }
    }

    // Link: [label](href "title")
    if (char === '[') {
      const labelEnd = findClosingBracket(content_, index + 1, '[', ']');
      if (labelEnd !== -1 && content_[labelEnd + 1] === '(') {
        const destEnd = findClosingBracket(content_, labelEnd + 2, '(', ')');
        if (destEnd !== -1) {
          const label = content_.slice(index + 1, labelEnd);
          const linkMetaRaw = content_.slice(labelEnd + 2, destEnd);
          const linkMeta = parseLinkDestination(linkMetaRaw.trim());
          pushText();
          nodes.push(
            createNode(
              NodeType.LINK,
              {
                href: linkMeta.href,
                title: linkMeta.title,
              },
              parseInline(label)
            )
          );
          index = destEnd + 1;
          continue;
        }
      }
    }

    // Inline code : `code` or ```` fenced ````
    if (char === '`') {
      let tickCount = 1;
      while (
        index + tickCount < length &&
        content_[index + tickCount] === '`'
      ) {
        tickCount += 1;
      }
      const marker = '`'.repeat(tickCount);
      const closingIndex = findClosingSequence(
        content_,
        index + tickCount,
        marker
      );
    if (closingIndex !== -1) {
      const rawValue = content_.slice(index + tickCount, closingIndex);
      pushText();
      const normalized = normaliseCodeSpan(rawValue);
      // Inline code must not be parsed as markdown; keep raw text.
      nodes.push(createNode(NodeType.CODE_SPAN, { value: normalized }, []));
      index = closingIndex + tickCount;
      continue;
    }
  }

    const pair = content_.slice(index, index + 2);
    const handler = delimiterHandlers[pair];
    if (handler && handler()) {
      continue;
    }

    if ((char === '*' || char === '_') && handleEmphasisAndStrong(char)) {
      continue;
    }

    // Plain text
    textBuffer += char;
    index += 1;
  }

  pushText();
  return applyInlineColorMetadata(nodes);
}

export function buildHeadingNode(token_) {
  return createNode(
    NodeType.HEADING,
    { depth: token_.depth, color: token_.color ?? null },
    parseInline(token_.content)
  );
}

export function buildParagraphNode(token_) {
  return createNode(NodeType.PARAGRAPH, {}, parseInline(token_.content));
}

export function buildBlockquoteNode(token_, helpers_ = {}) {
  const tokenize = helpers_.tokenize;
  const buildDocumentTree = helpers_.buildDocumentTree;
  if (!tokenize || !buildDocumentTree) {
    return createNode(NodeType.BLOCKQUOTE, {}, []);
  }
  const innerTokens = tokenize(token_.content);
  const innerTree = buildDocumentTree(innerTokens);
  const innerChildren = innerTree.children ? [...innerTree.children] : [];
  return createNode(
    NodeType.BLOCKQUOTE,
    { color: token_.color ?? null },
    innerChildren
  );
}

export function buildListNode(token_, helpers_ = {}) {
  const listType =
    token_.type === BlockTokenType.ORDERED_LIST
      ? NodeType.ORDERED_LIST
      : NodeType.UNORDERED_LIST;
  const tokenize = helpers_.tokenize;
  const buildDocumentTree = helpers_.buildDocumentTree;
  const listChildren = (token_.items ?? []).map(itemEntry => {
    const itemContent =
      typeof itemEntry === 'string' ? itemEntry : itemEntry?.content ?? '';
    const itemColor =
      typeof itemEntry === 'object' ? itemEntry?.color ?? null : null;
    if (tokenize && buildDocumentTree) {
      const itemTokens = tokenize(itemContent);
      const itemTree = buildDocumentTree(itemTokens);
      const childNodes = itemTree.children ? [...itemTree.children] : [];
      if (
        childNodes.length === 1 &&
        childNodes[0].type === NodeType.PARAGRAPH
      ) {
        return createNode(NodeType.LIST_ITEM, { color: itemColor }, [
          ...(childNodes[0].children ?? []),
        ]);
      }
      return createNode(NodeType.LIST_ITEM, { color: itemColor }, childNodes);
    }
    return createNode(NodeType.LIST_ITEM, { color: itemColor }, [
      createNode(NodeType.TEXT, { value: itemContent }),
    ]);
  });
  const props = {};
  if (token_.type === BlockTokenType.ORDERED_LIST && token_.start > 1) {
    props.start = token_.start;
  }
  return createNode(listType, props, listChildren);
}

export function buildFencedCodeNode(token_) {
  return createNode(
    NodeType.FENCED_CODE,
    {
      lang: token_.lang,
      content: token_.content ?? '',
    },
    []
  );
}

export function buildIndentedCodeNode(token_) {
  return createNode(
    NodeType.INDENTED_CODE,
    { content: token_.content ?? '' },
    []
  );
}

export function buildThematicBreakNode(token_) {
  return createNode(NodeType.THEMATIC_BREAK, {
    color: token_?.color ?? null,
  });
}

export function buildTableNode(token_) {
  const headerCells = token_.header.map((cell, idx) =>
    createNode(
      NodeType.TABLE_CELL,
      { align: token_.align[idx] ?? null, header: true },
      parseInline(cell)
    )
  );
  const headerRow = createNode(NodeType.TABLE_ROW, {}, headerCells);
  const header = createNode(NodeType.TABLE_HEADER, {}, [headerRow]);

  const bodyRows = (token_.rows ?? []).map((rowEntry) => {
    const rowPayload = Array.isArray(rowEntry)
      ? { cells: rowEntry, color: null }
      : rowEntry || {};
    const rowCellsData = Array.isArray(rowPayload.cells)
      ? [...rowPayload.cells]
      : [];
    let rowColor = null;
    if (rowCellsData.length > 0 && !rowPayload.color) {
      const { text, color } = peelValidColorToken(
        rowCellsData[rowCellsData.length - 1]
      );
      if (color) {
        rowCellsData[rowCellsData.length - 1] = text;
        rowColor = color;
      }
    } else if (rowPayload.color) {
      rowColor = rowPayload.color;
    }
    const rowCells = rowCellsData.map((cell, idx) =>
      createNode(
        NodeType.TABLE_CELL,
        { align: token_.align[idx] ?? null, header: false },
        parseInline(cell)
      )
    );
    return createNode(
      NodeType.TABLE_ROW,
      rowColor ? { color: rowColor } : {},
      rowCells
    );
  });
  const body = createNode(NodeType.TABLE_BODY, {}, bodyRows);

  return createNode(NodeType.TABLE, { align: token_.align }, [header, body]);
}

export function buildCollapseNode(token_, helpers_ = {}) {
  const tokenize = helpers_.tokenize;
  const buildDocumentTree = helpers_.buildDocumentTree;
  if (!tokenize || !buildDocumentTree) {
    return createNode(NodeType.COLLAPSE, {
      title: token_?.title ?? 'Section',
      color: token_?.color ?? null,
    });
  }
  const innerTokens = tokenize(token_?.content ?? '');
  const innerTree = buildDocumentTree(innerTokens);
  const innerChildren = innerTree.children ? [...innerTree.children] : [];
  return createNode(
    NodeType.COLLAPSE,
    {
      title: token_?.title ?? 'Section',
      color: token_?.color ?? null,
    },
    innerChildren
  );
}

export function renderDocument(node_, helpers_) {
  return helpers_?.renderChildren ? helpers_.renderChildren(node_) : '';
}

export function renderHeading(node_, helpers_) {
  const renderChildren = helpers_?.renderChildren ?? (() => '');
  const depth = node_.props.depth ?? 1;
  const color = getColorConfig(node_.props?.color);
  const content = renderChildren(node_).trim();
  const textClass = color ? ` ${color.textClass}` : '';
  if (depth === 1) {
    const lineClass = color ? color.lineBgClass : 'bg-contrast bg-opacity-50';
    return `<div class="flex gap-05 items-center font-condensed uppercase mt-1 mb-05 text-2xl${textClass}"><div class="flex-1 ${lineClass}" style="height: 1px"></div>${content}<div class="flex-1 ${lineClass}" style="height: 1px"></div></div>`;
  }
  if (depth === 2) {
    const lineClass = color ? color.lineBgClass : 'bg-contrast bg-opacity-30';
    return `<div class="flex gap-1 items-center font-condensed uppercase text-xl mt-1 mb-05${textClass}">${content}<div class="flex-1 ${lineClass}" style="height: 1px"></div></div>`;
  }
  if (depth === 3) {
    return `<div class="text-lg font-bold font-condensed uppercase mt-1${textClass}">${content}</div>`;
  }
  return `<h${depth}>${content}</h${depth}>`;
}

export function renderParagraph(node_, helpers_) {
  const renderChildren = helpers_?.renderChildren ?? (() => '');
  const renderNodeHelper = helpers_?.renderNode ?? (() => '');
  const children = node_.children ?? [];
  const nonBreakChildren = children.filter(
    child => child && child.type !== NodeType.LINE_BREAK
  );
  if (
    nonBreakChildren.length > 0 &&
    nonBreakChildren.every(child => child.type === NodeType.CHECKBOX)
  ) {
    const checkboxHtml = nonBreakChildren
      .map(child => renderNodeHelper(child))
      .join('');
    return `<div class="m-0">${checkboxHtml}</div>`;
  }
  const trimmed = trimEdgeLineBreaks(children);
  if (trimmed.length !== children.length) {
    const inner = trimmed.map(child => renderNodeHelper(child)).join('');
    return `<p>${inner}</p>`;
  }
  return `<p>${renderChildren(node_)}</p>`;
}

export function renderBlockquote(node_, helpers_) {
  const renderChildren = helpers_?.renderChildren ?? (() => '');
  const content = renderChildren(node_).trim();
  const color = getColorConfig(node_.props?.color);
  const borderClass = color
    ? `${color.borderClass} border-opacity-80`
    : 'border-contrast border-opacity-50';
  return `<div class="border-l-2 ${borderClass} pl-1 mx-1 opacity-70 text-left">${content}</div>`;
}

export function renderOrderedList(node_, helpers_) {
  const start =
    node_.props?.start && node_.props.start > 0 ? node_.props.start : 1;
  const children = node_.children ?? [];
  let index = start;
  return children
    .map(child =>
      renderCustomListItem(child, helpers_, {
        ordered: true,
        index: index++,
      })
    )
    .join('');
}

export function renderUnorderedList(node_, helpers_) {
  const children = node_.children ?? [];
  return children
    .map(child =>
      renderCustomListItem(child, helpers_, {
        ordered: false,
      })
    )
    .join('');
}

export function renderListItem(node_, helpers_) {
  const renderChildren = helpers_?.renderChildren ?? (() => '');
  return `<li>${renderChildren(node_)}</li>`;
}

export function renderFencedCode(node_, helpers_) {
  const language = node_.props?.lang;
  const escapeHtml = helpers_?.escapeHtml ?? ((value) => value);
  const classAttr = language ? ` class="language-${escapeHtml(language)}"` : '';
  const content = escapeHtml(`${node_?.props?.content ?? ''}`);
  return `<pre><code${classAttr}>${content}</code></pre>`;
}

export function renderIndentedCode(node_, helpers_) {
  return renderFencedCode(node_, helpers_);
}

export function renderThematicBreak(node_) {
  const color = getColorConfig(node_?.props?.color);
  const hrClass = color
    ? `${color.hrBorderClass} opacity-50`
    : 'border-contrast opacity-30';
  return `<hr class="${hrClass} mt-03 mb-03" />`;
}

export function renderTable(node_, helpers_) {
  const renderChildren = helpers_?.renderChildren ?? (() => '');
  return `<div class="overflow-x-auto"><table class="w-full">${renderChildren(node_)}</table></div>`;
}

export function renderTableHeader(node_, helpers_) {
  const renderChildren = helpers_?.renderChildren ?? (() => '');
  return `<thead>${renderChildren(node_)}</thead>`;
}

export function renderTableBody(node_, helpers_) {
  const renderChildren = helpers_?.renderChildren ?? (() => '');
  return `<tbody>${renderChildren(node_)}</tbody>`;
}

export function renderTableRow(node_, helpers_) {
  const renderChildren = helpers_?.renderChildren ?? (() => '');
  const color = getColorConfig(node_.props?.color);
  const classNames = [];
  if (color?.lineBgClass) {
    classNames.push(`${color.lineBgClass} bg-opacity-40`);
  }
  const classAttr = classNames.length ? ` class="${classNames.join(' ')}"` : '';
  return `<tr${classAttr}>${renderChildren(node_)}</tr>`;
}

export function renderTableCell(node_, helpers_) {
  const renderChildren = helpers_?.renderChildren ?? (() => '');
  const tag = node_.props?.header ? 'th' : 'td';
  const align = node_.props?.align;
  const alignAttr = align ? ` align="${align}"` : '';
  return `<${tag}${alignAttr}>${renderChildren(node_)}</${tag}>`;
}

export function renderCollapse(node_, helpers_) {
  const renderChildren = helpers_?.renderChildren ?? (() => '');
  const renderNodeHelper = helpers_?.renderNode ?? (() => '');
  const titleRaw = node_?.props?.title ?? 'Section';
  const titleNodes = parseInline(titleRaw);
  const title = titleNodes.map(child => renderNodeHelper(child)).join('');
  const color = getColorConfig(node_?.props?.color);
  const borderClass = color
    ? `${color.borderClass} border-opacity-60`
    : 'border-primary border-opacity-25';
  const summaryClass = color
    ? `${color.textClass}`
    : 'text-contrast text-opacity-80';
  const bgClass = color
    ? `${color.lineBgClass} bg-opacity-10`
    : 'bg-main bg-opacity-30';

  return `<details class="md-collapse rounded border ${borderClass} ${bgClass} my-05 p-05 overflow-hidden"><summary class="md-collapse-summary cursor-pointer select-none px-075 py-05 font-condensed font-bold uppercase text-base ${summaryClass}">${title}</summary><div class="md-collapse-content px-075 py-05">${renderChildren(
    node_
  )}</div></details>`;
}

export function renderText(node_, helpers_) {
  const value = node_.props?.value ?? '';
  return value;
}

export function renderEmphasis(node_, helpers_) {
  const renderChildren = helpers_?.renderChildren ?? (() => '');
  const color = getColorConfig(node_.props?.color);
  const classAttr = color ? ` class="${color.textClass}"` : '';
  return `<em${classAttr}>${renderChildren(node_)}</em>`;
}

export function renderStrong(node_, helpers_) {
  const renderChildren = helpers_?.renderChildren ?? (() => '');
  const color = getColorConfig(node_.props?.color);
  if (color) {
    return `<strong class="font-weight ${color.textClass}">${renderChildren(
      node_
    )}</strong>`;
  }
  return `<strong>${renderChildren(node_)}</strong>`;
}

export function renderUnderline(node_, helpers_) {
  const renderChildren = helpers_?.renderChildren ?? (() => '');
  const color = getColorConfig(node_.props?.color);
  const className = color ? `underline ${color.textClass}` : 'underline';
  return `<span class="${className}">${renderChildren(node_)}</span>`;
}

export function renderStrikethrough(node_, helpers_) {
  const renderChildren = helpers_?.renderChildren ?? (() => '');
  const color = getColorConfig(node_.props?.color);
  const classAttr = color ? ` class="${color.textClass}"` : '';
  return `<strike${classAttr}>${renderChildren(node_)}</strike>`;
}

export function renderHighlight(node_, helpers_) {
  const renderChildren = helpers_?.renderChildren ?? (() => '');
  const color = getColorConfig(node_.props?.color);
  const classes = color
    ? `text-contrast text-opacity-75 ${color.markBgClass} px-03 py-01 rounded`
    : 'text-contrast text-opacity-75 bg-contrast bg-opacity-30 px-03 py-01 rounded';
  return `<mark class="${classes}">${renderChildren(node_)}</mark>`;
}

export function renderIcon(node_) {
  const slug = node_.props?.slug;
  if (!slug) {
    return '';
  }
  return `<span class="inline-flex align-middle" data-icon="${slug}"></span>`;
}

export function renderSelect(node_, helpers_) {
  const escapeHtml = helpers_?.escapeHtml ?? (value => value);
  const current = `${node_?.props?.current ?? ''}`;
  const options = Array.isArray(node_?.props?.options)
    ? node_.props.options.map(option => `${option}`)
    : [];
  if (!current || options.length === 0) {
    return '';
  }
  const tokenBody = `${current}|${options.join('|')}`;
  const encodeAttr = value => {
    if (!value) {
      return '';
    }
    return encodeURIComponent(value);
  };
  return `<span class="inline-flex align-middle"><select data-md-select="true" data-current="${encodeAttr(
    current
  )}" data-options="${encodeAttr(JSON.stringify(options))}" data-token="${encodeAttr(
    tokenBody
  )}" class="board-grid-no-drag bg-main bg-opacity-40 border border-primary border-opacity-20 rounded px-03 py-01 text-xs text-contrast">${options
    .map(option => {
      const safe = escapeHtml(option);
      const selected = option === current ? ' selected' : '';
      return `<option value="${safe}"${selected}>${safe}</option>`;
    })
    .join('')}</select></span>`;
}

export function renderCodeSpan(node_, helpers_) {
  const escapeHtml = helpers_?.escapeHtml ?? (value => value);
  const value = escapeHtml(`${node_?.props?.value ?? ''}`);
  return `<code>${value}</code>`;
}

export function renderLineBreak() {
  return '<br />';
}

export function renderImage(node_, helpers_) {
  const escapeHtml = helpers_?.escapeHtml ?? (value => value);
  const alt = escapeHtml(node_.props?.alt ?? '');
  const src = escapeHtml(node_.props?.src ?? '');
  const title = escapeHtml(node_.props?.title ?? '');
  const extraClass = node_.props?.classes ?? '';
  const titleAttr = title ? ` title="${title}"` : '';
  const classAttr = extraClass
    ? ` class="icon24 ${extraClass}"`
    : ` class="icon24"`;
  return `<img${classAttr} src="${src}" alt="${alt}"${titleAttr} />`;
}

export function renderCheckbox(node_, helpers_) {
  const renderChildren = helpers_?.renderChildren ?? (() => '');
  const escapeHtml = helpers_?.escapeHtml ?? (value => value);
  const labelHtml = renderChildren(node_);
  const rawLabel = node_.props?.rawLabel ?? '';
  const encodeAttr = value => {
    if (!value) {
      return '';
    }
    return encodeURIComponent(value);
  };
  const encodedContent = encodeAttr(labelHtml || escapeHtml(rawLabel));
  const encodedRaw = encodeAttr(rawLabel);
  const isChecked = node_.props?.checked ? 'true' : 'false';
  return `<input type="checkbox" ischecked="${isChecked}" data-content="${encodedContent}" data-raw="${encodedRaw}" />`;
}

export function renderLink(node_, helpers_) {
  const renderChildren = helpers_?.renderChildren ?? (() => '');
  const escapeHtml = helpers_?.escapeHtml ?? (value => value);
  const href = escapeHtml(node_.props?.href ?? '');
  const title = node_.props?.title
    ? ` title="${escapeHtml(node_.props.title)}"`
    : '';
  const inner = renderChildren(node_).trim();
  const isExternal = /^https?:\/\//i.test(href);
  const color = getColorConfig(node_.props?.color);
  const colorClass = color ? color.textClass : '';

  if (isExternal) {
    const classNames = [
      'underline',
      'cursor-pointer',
      'transition',
      'opacity-100',
      'hover:opacity-50',
    ];
    if (colorClass) {
      classNames.push(colorClass);
    }
    return `<a class="${classNames.join(
      ' '
    )}" target="_blank" rel="noreferrer" href="${href}"${title}>${inner}</a>`;
  }

  const classNames = [
    'underline',
    'font-bold',
    'cursor-pointer',
    'transition',
    'opacity-100',
    'hover:opacity-50',
  ];
  if (colorClass) {
    classNames.push(colorClass);
  } else {
    classNames.push('text-contrast');
  }
  return `<a class="${classNames.join(
    ' '
  )}" id="item-link" href="${href}"${title}>@${inner}</a>`;
}

// Central registry describing how each node type participates in the parsing
// pipeline; handlers are stored directly for easier reuse.
export const MarkdownNodeBlueprint = Object.freeze({
  [NodeType.DOCUMENT]: {
    key: NodeType.DOCUMENT,
    detector: null,
    builder: null,
    renderer: renderDocument,
  },
  [NodeType.HEADING]: {
    key: NodeType.HEADING,
    detector: consumeHeading,
    builder: buildHeadingNode,
    renderer: renderHeading,
  },
  [NodeType.PARAGRAPH]: {
    key: NodeType.PARAGRAPH,
    detector: null,
    builder: buildParagraphNode,
    renderer: renderParagraph,
  },
  [NodeType.BLOCKQUOTE]: {
    key: NodeType.BLOCKQUOTE,
    detector: consumeBlockquote,
    builder: buildBlockquoteNode,
    renderer: renderBlockquote,
  },
  [NodeType.ORDERED_LIST]: {
    key: NodeType.ORDERED_LIST,
    detector: consumeList,
    builder: buildListNode,
    renderer: renderOrderedList,
  },
  [NodeType.UNORDERED_LIST]: {
    key: NodeType.UNORDERED_LIST,
    detector: consumeList,
    builder: buildListNode,
    renderer: renderUnorderedList,
  },
  [NodeType.LIST_ITEM]: {
    key: NodeType.LIST_ITEM,
    detector: null,
    builder: null,
    renderer: renderListItem,
  },
  [NodeType.FENCED_CODE]: {
    key: NodeType.FENCED_CODE,
    detector: consumeFencedCode,
    builder: buildFencedCodeNode,
    renderer: renderFencedCode,
  },
  [NodeType.INDENTED_CODE]: {
    key: NodeType.INDENTED_CODE,
    detector: consumeIndentedCode,
    builder: buildIndentedCodeNode,
    renderer: renderIndentedCode,
  },
  [NodeType.THEMATIC_BREAK]: {
    key: NodeType.THEMATIC_BREAK,
    detector: consumeThematicBreak,
    builder: buildThematicBreakNode,
    renderer: renderThematicBreak,
  },
  [NodeType.TABLE]: {
    key: NodeType.TABLE,
    detector: consumeTable,
    builder: buildTableNode,
    renderer: renderTable,
  },
  [NodeType.TABLE_HEADER]: {
    key: NodeType.TABLE_HEADER,
    detector: null,
    builder: null,
    renderer: renderTableHeader,
  },
  [NodeType.TABLE_BODY]: {
    key: NodeType.TABLE_BODY,
    detector: null,
    builder: null,
    renderer: renderTableBody,
  },
  [NodeType.TABLE_ROW]: {
    key: NodeType.TABLE_ROW,
    detector: null,
    builder: null,
    renderer: renderTableRow,
  },
  [NodeType.TABLE_CELL]: {
    key: NodeType.TABLE_CELL,
    detector: null,
    builder: null,
    renderer: renderTableCell,
  },
  [NodeType.COLLAPSE]: {
    key: NodeType.COLLAPSE,
    detector: consumeCollapse,
    builder: buildCollapseNode,
    renderer: renderCollapse,
  },
  [NodeType.TEXT]: {
    key: NodeType.TEXT,
    detector: null,
    builder: null,
    renderer: renderText,
  },
  [NodeType.EMPHASIS]: {
    key: NodeType.EMPHASIS,
    detector: null,
    builder: null,
    renderer: renderEmphasis,
  },
  [NodeType.STRONG]: {
    key: NodeType.STRONG,
    detector: null,
    builder: null,
    renderer: renderStrong,
  },
  [NodeType.UNDERLINE]: {
    key: NodeType.UNDERLINE,
    detector: null,
    builder: null,
    renderer: renderUnderline,
  },
  [NodeType.STRIKETHROUGH]: {
    key: NodeType.STRIKETHROUGH,
    detector: null,
    builder: null,
    renderer: renderStrikethrough,
  },
  [NodeType.HIGHLIGHT]: {
    key: NodeType.HIGHLIGHT,
    detector: null,
    builder: null,
    renderer: renderHighlight,
  },
  [NodeType.CODE_SPAN]: {
    key: NodeType.CODE_SPAN,
    detector: null,
    builder: null,
    renderer: renderCodeSpan,
  },
  [NodeType.LINE_BREAK]: {
    key: NodeType.LINE_BREAK,
    detector: null,
    builder: null,
    renderer: renderLineBreak,
  },
  [NodeType.IMAGE]: {
    key: NodeType.IMAGE,
    detector: null,
    builder: null,
    renderer: renderImage,
  },
  [NodeType.CHECKBOX]: {
    key: NodeType.CHECKBOX,
    detector: null,
    builder: null,
    renderer: renderCheckbox,
  },
  [NodeType.LINK]: {
    key: NodeType.LINK,
    detector: null,
    builder: null,
    renderer: renderLink,
  },
  [NodeType.ICON]: {
    key: NodeType.ICON,
    detector: null,
    builder: null,
    renderer: renderIcon,
  },
  [NodeType.SELECT]: {
    key: NodeType.SELECT,
    detector: null,
    builder: null,
    renderer: renderSelect,
  },
});
function trimEdgeLineBreaks(children = []) {
  if (!Array.isArray(children) || children.length === 0) {
    return [];
  }
  let start = 0;
  let end = children.length - 1;
  while (
    start <= end &&
    children[start] &&
    children[start].type === NodeType.LINE_BREAK
  ) {
    start += 1;
  }
  while (
    end >= start &&
    children[end] &&
    children[end].type === NodeType.LINE_BREAK
  ) {
    end -= 1;
  }
  if (start === 0 && end === children.length - 1) {
    return children;
  }
  return children.slice(start, end + 1);
}

function unwrapParagraph(html = '') {
  const match = html.match(/^<p>([\s\S]*)<\/p>$/);
  if (!match) {
    return html;
  }
  return match[1].trim();
}

function renderCustomListItem(node_, helpers_, options = {}) {
  const renderChildren = helpers_?.renderChildren ?? (() => '');
  const innerHtml = renderChildren(node_).trim();
  if (!innerHtml) {
    return '';
  }
  const content = unwrapParagraph(innerHtml);
  const color = getColorConfig(node_.props?.color);
  if (options.ordered) {
    const label = `${options.index}.`;
    const numberClass = color
      ? `${color.textClass} text-sm mt-03`
      : 'text-contrast text-opacity-75 text-sm mt-03';
    return `<div class="pl-1 flex gap-05"><div class="${numberClass}">${label}</div><div>${content}</div></div>`;
  }
  const bulletClass = color ? color.textClass : 'text-contrast text-opacity-75';
  return `<div class="pl-1 flex gap-05"><div class="${bulletClass}">○</div><div>${content}</div></div>`;
}
